import api from "../config/api";
import { getErrorMessage } from "../config/errorHelper";

// ─────────────────────────────────────────────
//  NOTE : pas besoin d'ajouter le token manuellement ici,
//  api.js l'injecte automatiquement (voir config/api.js).
// ─────────────────────────────────────────────

// Une sync interroge l'API Site Manager (UniFi) ou se connecte en direct
// à chaque NAS tour à tour (Synology) — ça peut prendre plusieurs secondes
// dès qu'il y a plus de quelques appareils, d'où un timeout plus généreux
// que les 10s par défaut de l'instance axios.
const SYNC_TIMEOUT = 60000;

// ─────────────────────────────────────────────
//  VUE D'ENSEMBLE — nas + gateways en un seul appel
// ─────────────────────────────────────────────

export const getOverview = async () => {
  try {
    const res = await api.get("/monitoring/admin/overview");
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  NAS SYNOLOGY
// ─────────────────────────────────────────────

export const getNasList = async () => {
  try {
    const res = await api.get("/monitoring/admin/nas");
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** payload : { companyId, name, quickConnectHost, credentialId } */
export const registerNas = async (payload) => {
  try {
    const res = await api.post("/monitoring/admin/nas", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** payload : { companyId?, name?, quickConnectHost?, credentialId? } — champs omis = inchangés */
export const updateNas = async (id, payload) => {
  try {
    const res = await api.put(`/monitoring/admin/nas/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

export const deleteNas = async (id) => {
  try {
    const res = await api.delete(`/monitoring/admin/nas/${id}`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Déclenche la sync de tous les NAS enregistrés. @returns { synced, failed, total } */
export const syncNasNow = async () => {
  try {
    const res = await api.post("/monitoring/sync/nas", {}, { timeout: SYNC_TIMEOUT });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Déclenche la sync d'un seul NAS (bouton par ligne). @returns le NAS mis à jour */
export const syncOneNas = async (id) => {
  try {
    const res = await api.post(`/monitoring/sync/nas/${id}`, {}, { timeout: SYNC_TIMEOUT });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * DIAGNOSTIC — réponse brute des APIs de sauvegarde (Hyper Backup / Active
 * Backup for Business) pour un NAS précis. À utiliser une fois par NAS pour
 * confirmer les vrais noms de champs avant de finir le mapping statut/date/
 * durée affiché dans la section "Sauvegardes".
 */
export const getNasBackupsRaw = async (id) => {
  try {
    const res = await api.get(`/monitoring/admin/nas/${id}/backups-raw`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  PASSERELLES UNIFI
// ─────────────────────────────────────────────

export const getGatewayList = async () => {
  try {
    const res = await api.get("/monitoring/admin/gateways");
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** payload : { companyId, name, unifiHostId } */
export const registerGateway = async (payload) => {
  try {
    const res = await api.post("/monitoring/admin/gateways", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** payload : { companyId?, name?, unifiHostId? } — champs omis = inchangés */
export const updateGateway = async (id, payload) => {
  try {
    const res = await api.put(`/monitoring/admin/gateways/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

export const deleteGateway = async (id) => {
  try {
    const res = await api.delete(`/monitoring/admin/gateways/${id}`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Déclenche la sync de toutes les passerelles enregistrées. @returns { synced, unmatched, total } */
export const syncGatewaysNow = async () => {
  try {
    const res = await api.post("/monitoring/sync/gateways", {}, { timeout: SYNC_TIMEOUT });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Déclenche la sync d'une seule passerelle (bouton par ligne). @returns la passerelle mise à jour */
export const syncOneGateway = async (id) => {
  try {
    const res = await api.post(`/monitoring/sync/gateways/${id}`, {}, { timeout: SYNC_TIMEOUT });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Découverte en direct des hosts vus par l'API Site Manager UniFi — SANS
 * aucun enregistrement préalable. Sert à peupler le sélecteur du modal
 * "Ajouter une passerelle" pour ne jamais avoir à copier un unifiHostId
 * à la main depuis Postman.
 * @returns {Array<{ id, name, model, ipAddress, online }>}
 */
export const discoverUnifiHosts = async () => {
  try {
    const res = await api.get("/monitoring/admin/unifi/discover");
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data?.data ?? res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};